import React, { useState, useMemo, useCallback } from 'react';
import { Clock, BriefcaseIcon, Utensils, BookOpen, Smartphone, Moon, Tv, Users, Droplet, Wind, Footprints, RotateCcw, ChevronDown, ChevronUp } from 'lucide-react';
import { LifeStats } from '../../hooks/useLifeStats';
import Card from '../Card';
import FunctionHealthMiniCTA from '../FunctionHealthMiniCTA';

interface DailyViewProps {
  stats: LifeStats;
}

// Default time allocation constants (hours per day) with explicit colors for SVG
const DEFAULT_ACTIVITIES = [
  { name: "Sleep", hours: 8, icon: Moon, color: "indigo", fill: "#818cf8", fillDark: "#6366f1", fillSelected: "#4f46e5" },
  { name: "Work/Study", hours: 8, icon: BriefcaseIcon, color: "blue", fill: "#60a5fa", fillDark: "#3b82f6", fillSelected: "#2563eb" },
  { name: "Meals & Breaks", hours: 2, icon: Utensils, color: "orange", fill: "#fb923c", fillDark: "#f97316", fillSelected: "#ea580c" },
  { name: "Commute", hours: 1, icon: Clock, color: "gray", fill: "#9ca3af", fillDark: "#6b7280", fillSelected: "#4b5563" },
  { name: "Exercise", hours: 0.5, icon: Users, color: "green", fill: "#4ade80", fillDark: "#22c55e", fillSelected: "#16a34a" },
  { name: "Digital Media", hours: 2, icon: Smartphone, color: "pink", fill: "#f472b6", fillDark: "#ec4899", fillSelected: "#db2777" },
  { name: "TV/Entertainment", hours: 1.5, icon: Tv, color: "purple", fill: "#a78bfa", fillDark: "#8b5cf6", fillSelected: "#7c3aed" },
  { name: "Reading/Learning", hours: 0.5, icon: BookOpen, color: "yellow", fill: "#facc15", fillDark: "#eab308", fillSelected: "#ca8a04" },
  { name: "Social Time", hours: 0.5, icon: Users, color: "red", fill: "#f87171", fillDark: "#ef4444", fillSelected: "#dc2626" }
];

const DailyView: React.FC<DailyViewProps> = ({ stats }) => {
  const [selectedActivity, setSelectedActivity] = useState<string | null>(null);
  const [isDark, setIsDark] = useState(false);
  const [customizeExpanded, setCustomizeExpanded] = useState(false);
  const [hoveredActivity, setHoveredActivity] = useState<string | null>(null);

  // Activity hours state - initialized from defaults
  const [activityHours, setActivityHours] = useState<Record<string, number>>(() => {
    const initial: Record<string, number> = {};
    DEFAULT_ACTIVITIES.forEach(a => {
      initial[a.name] = a.hours;
    });
    return initial;
  });

  // Detect dark mode
  React.useEffect(() => {
    const checkDark = () => {
      setIsDark(document.documentElement.classList.contains('dark'));
    };
    checkDark();
    const observer = new MutationObserver(checkDark);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  // Calculate total hours
  const totalHours = useMemo(() => {
    return Object.values(activityHours).reduce((sum, h) => sum + h, 0);
  }, [activityHours]);

  // Get activities with current hours
  const activities = useMemo(() => {
    return DEFAULT_ACTIVITIES.map(a => ({
      ...a,
      hours: activityHours[a.name]
    }));
  }, [activityHours]);

  // Handle slider change
  const handleHoursChange = useCallback((name: string, value: number) => {
    setActivityHours(prev => ({
      ...prev,
      [name]: value
    }));
  }, []);

  // Reset to defaults
  const handleReset = useCallback(() => {
    const defaults: Record<string, number> = {};
    DEFAULT_ACTIVITIES.forEach(a => {
      defaults[a.name] = a.hours;
    });
    setActivityHours(defaults);
  }, []);

  // Check if values differ from defaults
  const hasChanges = useMemo(() => {
    return DEFAULT_ACTIVITIES.some(a => activityHours[a.name] !== a.hours);
  }, [activityHours]);

  if (stats.lifeExpectancy <= 0) return null;

  // Calculate accumulated time spent on activities
  const daysLived = stats.yearsLived * 365.25;

  // Generate clock segments with callout lines
  const generateClockSegments = () => {
    // Filter out activities with 0 hours
    const activeActivities = activities.filter(a => a.hours > 0);
    const activeTotalHours = activeActivities.reduce((sum, a) => sum + a.hours, 0);

    if (activeTotalHours === 0) return { segments: [], callouts: [] };

    const hoursToDegrees = (hours: number) => (hours / activeTotalHours) * 360;

    let startAngle = 0;
    const segments: React.ReactElement[] = [];
    const calloutData: { activity: typeof activeActivities[0]; labelAngle: number; pullOutX: number; pullOutY: number; isHighlighted: boolean }[] = [];

    const radius = 100;
    const centerX = 150; // Shifted right to give more room on left
    const centerY = 120; // Shifted down to give more room on top

    activeActivities.forEach((activity) => {
      const segmentDegrees = hoursToDegrees(activity.hours);
      const endAngle = startAngle + segmentDegrees;
      const largeArcFlag = segmentDegrees > 180 ? 1 : 0;

      // Calculate coordinates
      const startX = centerX + radius * Math.sin((startAngle * Math.PI) / 180);
      const startY = centerY - radius * Math.cos((startAngle * Math.PI) / 180);
      const endX = centerX + radius * Math.sin((endAngle * Math.PI) / 180);
      const endY = centerY - radius * Math.cos((endAngle * Math.PI) / 180);

      // Create SVG arc path
      const path = `M ${centerX},${centerY} L ${startX},${startY} A ${radius},${radius} 0 ${largeArcFlag},1 ${endX},${endY} Z`;

      // Calculate label position
      const labelAngle = startAngle + segmentDegrees / 2;
      const labelRadius = radius * 0.65;
      const labelX = centerX + labelRadius * Math.sin((labelAngle * Math.PI) / 180);
      const labelY = centerY - labelRadius * Math.cos((labelAngle * Math.PI) / 180);

      // Determine if activity is selected or hovered
      const isSelected = selectedActivity === activity.name;
      const isHovered = hoveredActivity === activity.name;
      const isHighlighted = isSelected || isHovered;

      // Get the actual fill color
      const fillColor = isSelected ? activity.fillSelected : (isDark ? activity.fillDark : activity.fill);

      // Calculate pull-out effect for small or highlighted segments
      const isSmall = activity.hours < 1.5;
      const pullOutDistance = isHighlighted ? 8 : (isSmall ? 4 : 0);
      const pullOutX = pullOutDistance * Math.sin((labelAngle * Math.PI) / 180);
      const pullOutY = -pullOutDistance * Math.cos((labelAngle * Math.PI) / 180);

      // Create segment
      segments.push(
        <g
          key={activity.name}
          onClick={() => setSelectedActivity(isSelected ? null : activity.name)}
          onMouseEnter={() => setHoveredActivity(activity.name)}
          onMouseLeave={() => setHoveredActivity(null)}
          className="cursor-pointer"
          style={{
            transform: `translate(${pullOutX}px, ${pullOutY}px)`,
            transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1), filter 0.3s ease'
          }}
        >
          <path
            d={path}
            fill={fillColor}
            stroke={isDark ? "#45475a" : "#e5e7eb"}
            strokeWidth="2"
            style={{
              filter: isHighlighted ? 'brightness(1.15) drop-shadow(0 0 8px rgba(255,255,255,0.3))' : 'none',
              transition: 'filter 0.3s ease, d 0.5s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
          />
          {/* Only show label inside for larger segments */}
          {activity.hours >= 2.5 && (
            <text
              x={labelX}
              y={labelY}
              textAnchor="middle"
              dominantBaseline="middle"
              fill={isDark ? "#cdd6f4" : "#1f2937"}
              fontSize="8"
              fontWeight="600"
              className="pointer-events-none select-none"
              style={{ transition: 'opacity 0.3s ease' }}
            >
              {activity.name}
            </text>
          )}
        </g>
      );

      // Collect callout data for small segments (< 2.5 hours)
      if (activity.hours < 2.5 && activity.hours > 0) {
        calloutData.push({ activity, labelAngle, pullOutX, pullOutY, isHighlighted });
      }

      // Update starting angle for next segment
      startAngle = endAngle;
    });

    // Now generate callouts with collision avoidance
    const callouts: React.ReactElement[] = [];
    const minYGap = 16; // Minimum vertical gap between labels

    // Sort callouts by angle to process them in order (helps with collision detection)
    const sortedCallouts = [...calloutData].sort((a, b) => a.labelAngle - b.labelAngle);

    // Separate into left and right sides, then sort by Y position
    const leftCallouts = sortedCallouts.filter(c => c.labelAngle >= 180 || c.labelAngle === 0);
    const rightCallouts = sortedCallouts.filter(c => c.labelAngle > 0 && c.labelAngle < 180);

    // Process each side separately with proper spacing
    const processCallouts = (items: typeof calloutData, isRightSide: boolean) => {
      const usedYPositions: number[] = [];

      // Sort by initial Y position for better distribution
      const sorted = [...items].sort((a, b) => {
        const yA = centerY - (radius + 40) * Math.cos((a.labelAngle * Math.PI) / 180);
        const yB = centerY - (radius + 40) * Math.cos((b.labelAngle * Math.PI) / 180);
        return yA - yB;
      });

      sorted.forEach(({ activity, labelAngle, pullOutX, pullOutY, isHighlighted }) => {
        const calloutStartRadius = radius + 5;
        const calloutEndRadius = radius + 45;

        // Calculate initial position
        let calloutX2 = centerX + calloutEndRadius * Math.sin((labelAngle * Math.PI) / 180);
        let calloutY2 = centerY - calloutEndRadius * Math.cos((labelAngle * Math.PI) / 180);

        // Adjust Y to avoid collisions
        let adjustedY = calloutY2;
        let hasCollision = true;
        let iterations = 0;
        const maxIterations = 20;

        while (hasCollision && iterations < maxIterations) {
          hasCollision = false;
          for (const usedY of usedYPositions) {
            if (Math.abs(adjustedY - usedY) < minYGap) {
              hasCollision = true;
              // Move away from collision
              if (adjustedY <= usedY) {
                adjustedY = usedY - minYGap;
              } else {
                adjustedY = usedY + minYGap;
              }
              break;
            }
          }
          iterations++;
        }

        usedYPositions.push(adjustedY);
        calloutY2 = adjustedY;

        const calloutX1 = centerX + calloutStartRadius * Math.sin((labelAngle * Math.PI) / 180);
        const calloutY1 = centerY - calloutStartRadius * Math.cos((labelAngle * Math.PI) / 180);

        // Create an elbow path - first go radially out, then horizontally to the label
        const midRadius = radius + 25;
        const midX = centerX + midRadius * Math.sin((labelAngle * Math.PI) / 180);
        const midY = centerY - midRadius * Math.cos((labelAngle * Math.PI) / 180);

        // Horizontal extension for the elbow
        const horizontalExtend = isRightSide ? 20 : -20;
        const elbowX = midX + horizontalExtend * 0.5;

        // Text position - further out for more space
        const textX = isRightSide ? calloutX2 + 6 : calloutX2 - 6;

        callouts.push(
          <g key={`callout-${activity.name}`} style={{ transition: 'opacity 0.3s ease' }}>
            <path
              d={`M ${calloutX1 + pullOutX} ${calloutY1 + pullOutY} L ${midX} ${midY} Q ${elbowX} ${(midY + calloutY2) / 2} ${calloutX2} ${calloutY2}`}
              fill="none"
              stroke={isDark ? activity.fillDark : activity.fill}
              strokeWidth="1.5"
              strokeLinecap="round"
              opacity={isHighlighted ? 1 : 0.7}
              style={{ transition: 'opacity 0.3s ease' }}
            />
            <circle
              cx={calloutX2}
              cy={calloutY2}
              r="3"
              fill={isDark ? activity.fillDark : activity.fill}
              style={{ transition: 'fill 0.3s ease' }}
            />
            <text
              x={textX}
              y={calloutY2}
              textAnchor={isRightSide ? "start" : "end"}
              dominantBaseline="middle"
              fill={isDark ? "#cdd6f4" : "#374151"}
              fontSize="9"
              fontWeight={isHighlighted ? "600" : "500"}
              className="pointer-events-none select-none"
              style={{ transition: 'font-weight 0.3s ease' }}
            >
              {activity.name}
            </text>
          </g>
        );
      });
    };

    processCallouts(leftCallouts, false);
    processCallouts(rightCallouts, true);

    return { segments, callouts };
  };

  // Calculate lifetime stats
  const getLifetimeStats = (activityName: string) => {
    const hours = activityHours[activityName] || 0;

    const hoursSpent = daysLived * hours;
    const daysSpent = hoursSpent / 24;
    const yearsSpent = daysSpent / 365.25;
    const percentage = (hours / 24) * 100;

    return {
      days: Math.round(daysSpent),
      years: yearsSpent.toFixed(1),
      percentage: percentage.toFixed(1)
    };
  };

  // Daily average constants
  const DAILY_AVERAGES = [
    { label: "Water consumed", value: 2, unit: "liters", icon: Droplet, color: "blue" },
    { label: "Breaths taken", value: 20000, unit: "", icon: Wind, color: "cyan" },
    { label: "Steps walked", value: 7000, unit: "", icon: Footprints, color: "green" },
    { label: "Bathroom visits", value: 5, unit: "", icon: Droplet, color: "yellow" },
    { label: "Words spoken", value: 7000, unit: "", icon: Users, color: "purple" },
    { label: "Calories consumed", value: 2000, unit: "", icon: Utensils, color: "orange" }
  ];

  // Calculate lifetime totals for daily averages
  const getLifetimeTotal = (dailyAverage: number) => {
    return Math.floor(daysLived * dailyAverage).toLocaleString();
  };

  const { segments, callouts } = generateClockSegments();

  // Determine total hours status color
  const getTotalHoursColor = () => {
    if (totalHours === 24) return 'text-green-600 dark:text-catppuccin-green';
    if (totalHours > 24) return 'text-red-600 dark:text-catppuccin-red';
    return 'text-yellow-600 dark:text-catppuccin-yellow';
  };

  const getTotalHoursBgColor = () => {
    if (totalHours === 24) return 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800';
    if (totalHours > 24) return 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800';
    return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
  };

  return (
    <div className="w-full max-w-4xl mx-auto">
      <Card className="p-6" animated={true}>
        <div className="flex items-center gap-3 mb-6">
          <Clock size={24} className="text-blue-600 dark:text-catppuccin-blue" />
          <h2 className="text-xl font-bold dark:text-catppuccin-text">Your Daily Life</h2>
        </div>

        <p className="text-gray-600 dark:text-catppuccin-subtext0 mb-6">
          How we spend our days is how we spend our lives. This visualization shows your typical 24-hour day
          and how that adds up over {stats.yearsLived.toFixed(1)} years.
        </p>

        {/* Customization Panel */}
        <div className="mb-6">
          <button
            onClick={() => setCustomizeExpanded(!customizeExpanded)}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-catppuccin-surface1
                       rounded-lg hover:bg-gray-200 dark:hover:bg-catppuccin-surface2
                       transition-all duration-300 text-sm font-medium dark:text-catppuccin-text
                       border border-gray-200 dark:border-catppuccin-surface2"
          >
            {customizeExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            Customize Your Day
          </button>

          {customizeExpanded && (
            <div className="mt-4 p-4 bg-gray-50 dark:bg-catppuccin-surface0 rounded-lg border border-gray-200 dark:border-catppuccin-surface2
                          animate-in fade-in slide-in-from-top-2 duration-300">
              {/* Total Hours Indicator */}
              <div className={`flex items-center justify-between p-3 mb-4 rounded-lg border ${getTotalHoursBgColor()} transition-colors duration-300`}>
                <span className="text-sm font-medium dark:text-catppuccin-text">Total Hours</span>
                <div className="flex items-center gap-2">
                  <span className={`text-lg font-bold ${getTotalHoursColor()} transition-colors duration-300`}>
                    {totalHours.toFixed(1)} / 24
                  </span>
                  {totalHours !== 24 && (
                    <span className="text-xs text-gray-500 dark:text-catppuccin-subtext0">
                      ({totalHours > 24 ? '+' : ''}{(totalHours - 24).toFixed(1)} hrs)
                    </span>
                  )}
                </div>
              </div>

              {/* Progress bar for total hours */}
              <div className="mb-4">
                <div className="h-2 bg-gray-200 dark:bg-catppuccin-surface2 rounded-full overflow-hidden">
                  <div
                    className={`h-full transition-all duration-500 ease-out rounded-full ${
                      totalHours === 24 ? 'bg-green-500' : totalHours > 24 ? 'bg-red-500' : 'bg-yellow-500'
                    }`}
                    style={{ width: `${Math.min((totalHours / 24) * 100, 100)}%` }}
                  />
                </div>
              </div>

              {/* Sliders Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {activities.map(activity => {
                  const Icon = activity.icon;
                  const isSelected = selectedActivity === activity.name;
                  const isHovered = hoveredActivity === activity.name;

                  return (
                    <div
                      key={activity.name}
                      className={`p-3 rounded-lg transition-all duration-300 border
                                ${isSelected || isHovered
                                  ? 'bg-white dark:bg-catppuccin-surface1 border-gray-300 dark:border-catppuccin-surface2 shadow-md'
                                  : 'bg-transparent border-transparent hover:bg-white dark:hover:bg-catppuccin-surface1'}`}
                      onMouseEnter={() => setHoveredActivity(activity.name)}
                      onMouseLeave={() => setHoveredActivity(null)}
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <div
                          className="p-1.5 rounded-full transition-transform duration-300"
                          style={{
                            backgroundColor: `${isDark ? activity.fillDark : activity.fill}20`,
                            transform: isHovered ? 'scale(1.1)' : 'scale(1)'
                          }}
                        >
                          <Icon size={14} style={{ color: isDark ? activity.fillDark : activity.fill }} />
                        </div>
                        <span className="text-sm font-medium dark:text-catppuccin-text flex-1">{activity.name}</span>
                        <span
                          className="text-sm font-bold min-w-[3rem] text-right transition-all duration-300"
                          style={{ color: isDark ? activity.fillDark : activity.fill }}
                        >
                          {activity.hours}h
                        </span>
                      </div>

                      {/* Custom Slider */}
                      <div className="relative">
                        <input
                          type="range"
                          min="0"
                          max="12"
                          step="0.5"
                          value={activity.hours}
                          onChange={(e) => handleHoursChange(activity.name, parseFloat(e.target.value))}
                          className="w-full h-2 rounded-full appearance-none cursor-pointer
                                   bg-gray-200 dark:bg-catppuccin-surface2
                                   slider-thumb transition-all duration-200"
                          style={{
                            background: `linear-gradient(to right, ${isDark ? activity.fillDark : activity.fill} 0%, ${isDark ? activity.fillDark : activity.fill} ${(activity.hours / 12) * 100}%, ${isDark ? '#45475a' : '#e5e7eb'} ${(activity.hours / 12) * 100}%, ${isDark ? '#45475a' : '#e5e7eb'} 100%)`
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Reset Button */}
              {hasChanges && (
                <div className="mt-4 flex justify-end">
                  <button
                    onClick={handleReset}
                    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium
                             text-gray-600 dark:text-catppuccin-subtext0
                             hover:text-gray-900 dark:hover:text-catppuccin-text
                             bg-white dark:bg-catppuccin-surface1
                             border border-gray-200 dark:border-catppuccin-surface2
                             rounded-lg hover:shadow-md transition-all duration-300
                             hover:scale-105 active:scale-95"
                  >
                    <RotateCcw size={14} />
                    Reset to Defaults
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
          {/* Clock visualization */}
          <div className="relative flex flex-col items-center">
            <svg width="420" height="320" viewBox="-80 -10 440 280" className="mx-auto drop-shadow-lg">
              {/* Glow filter for highlights */}
              <defs>
                <filter id="glow" x="-50%" y="-50%" width="200%" height="200%">
                  <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>

              {segments}
              {callouts}

              {/* Center dot */}
              <circle cx="150" cy="120" r="4" className="fill-gray-800 dark:fill-catppuccin-text" />

              {/* Clock rim for reference */}
              <circle
                cx="150"
                cy="120"
                r="102"
                fill="none"
                stroke={isDark ? "#45475a" : "#e5e7eb"}
                strokeWidth="1"
                opacity="0.5"
              />
            </svg>

            <div className="mt-4 text-center text-gray-500 dark:text-catppuccin-subtext0 text-sm">
              <p>Click on a segment to see lifetime totals</p>
              <p className="text-xs mt-1">Hover to highlight</p>
            </div>
          </div>

          {/* Activity breakdown - Legend with stats */}
          <div>
            <h3 className="font-semibold mb-3 dark:text-catppuccin-text">Daily Time Allocation</h3>
            <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2">
              {activities.filter(a => a.hours > 0).map(activity => {
                const { days, years, percentage } = getLifetimeStats(activity.name);
                const Icon = activity.icon;
                const isSelected = selectedActivity === activity.name;
                const isHovered = hoveredActivity === activity.name;
                const isHighlighted = isSelected || isHovered;

                return (
                  <div
                    key={activity.name}
                    className={`p-3 rounded-lg transition-all duration-300 cursor-pointer border
                              ${isHighlighted
                                ? 'bg-white dark:bg-catppuccin-surface1 border-gray-200 dark:border-catppuccin-surface2 shadow-lg scale-[1.02]'
                                : 'bg-transparent border-transparent hover:bg-gray-50 dark:hover:bg-catppuccin-surface0'}`}
                    onClick={() => setSelectedActivity(isSelected ? null : activity.name)}
                    onMouseEnter={() => setHoveredActivity(activity.name)}
                    onMouseLeave={() => setHoveredActivity(null)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div
                          className="p-1.5 rounded-full transition-all duration-300"
                          style={{
                            backgroundColor: `${isDark ? activity.fillDark : activity.fill}${isHighlighted ? '40' : '20'}`,
                            transform: isHighlighted ? 'scale(1.15)' : 'scale(1)',
                            boxShadow: isHighlighted ? `0 0 12px ${isDark ? activity.fillDark : activity.fill}50` : 'none'
                          }}
                        >
                          <Icon size={14} style={{ color: isDark ? activity.fillDark : activity.fill }} />
                        </div>
                        <span className="text-sm font-medium dark:text-catppuccin-text">{activity.name}</span>
                      </div>
                      <span
                        className="text-sm font-bold transition-all duration-300"
                        style={{ color: isDark ? activity.fillDark : activity.fill }}
                      >
                        {activity.hours} hr{activity.hours !== 1 ? 's' : ''}
                      </span>
                    </div>

                    <div className="mt-2">
                      <div className="h-1.5 bg-gray-200 dark:bg-catppuccin-surface2 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500 ease-out"
                          style={{
                            width: `${(activity.hours / 12) * 100}%`,
                            backgroundColor: isDark ? activity.fillDark : activity.fill
                          }}
                        />
                      </div>
                    </div>

                    {isSelected && (
                      <div className="mt-3 text-xs text-gray-600 dark:text-catppuccin-subtext0 border-t border-gray-100 dark:border-catppuccin-surface2 pt-3
                                    animate-in fade-in slide-in-from-top-1 duration-200">
                        <p>You've spent approximately:</p>
                        <p className="font-semibold mt-1 dark:text-catppuccin-text text-sm">
                          {days.toLocaleString()} days ({years} years)
                          <span className="ml-1 opacity-75">({percentage}% of your life)</span>
                        </p>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Hidden activities notice */}
            {activities.filter(a => a.hours === 0).length > 0 && (
              <div className="mt-3 text-xs text-gray-400 dark:text-catppuccin-overlay0">
                {activities.filter(a => a.hours === 0).length} activities hidden (0 hours)
              </div>
            )}
          </div>
        </div>

        {/* Daily summary */}
        <div className="mt-10 text-center">
          <div className="inline-block bg-gray-50 dark:bg-catppuccin-surface1 px-6 py-3 rounded-lg">
            <h3 className="text-lg font-semibold mb-1 dark:text-catppuccin-text">In Your Lifetime So Far</h3>
            <p className="text-gray-600 dark:text-catppuccin-subtext0">
              You've lived approximately {Math.floor(daysLived).toLocaleString()} days,
              or {Math.floor(daysLived * 24).toLocaleString()} hours,
              or {Math.floor(daysLived * 24 * 60).toLocaleString()} minutes.
            </p>
          </div>

          {/* Lifetime averages */}
          <div className="mt-6 grid grid-cols-2 md:grid-cols-3 gap-4">
            {DAILY_AVERAGES.map(item => {
              const Icon = item.icon;
              return (
                <div key={item.label} className={`bg-${item.color}-50 dark:bg-catppuccin-surface1 p-4 rounded-lg border border-${item.color}-100 dark:border-catppuccin-surface2 transition-all duration-300 hover:scale-105 hover:shadow-lg`}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className={`p-1.5 rounded-full bg-${item.color}-100 dark:bg-catppuccin-surface2 text-${item.color}-600 dark:text-catppuccin-text`}>
                      <Icon size={16} />
                    </div>
                    <h4 className="font-medium text-sm dark:text-catppuccin-text">{item.label}</h4>
                  </div>
                  <p className="text-lg font-bold dark:text-catppuccin-text">{getLifetimeTotal(item.value)}</p>
                  <p className="text-xs text-gray-500 dark:text-catppuccin-subtext0">
                    Based on {item.value.toLocaleString()} {item.unit} daily average
                  </p>
                </div>
              );
            })}
          </div>

          <p className="text-xs text-gray-400 dark:text-catppuccin-overlay0 mt-4">
            *These calculations are based on statistical averages and may vary by individual.
          </p>

          {/* Health habits nudge */}
          <FunctionHealthMiniCTA
            className="mt-8"
            message="Understand how your daily habits affect your health"
            buttonText="Learn More"
          />
        </div>
      </Card>

      {/* Custom slider styles */}
      <style>{`
        input[type="range"]::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          border: 2px solid #3b82f6;
          box-shadow: 0 2px 6px rgba(0,0,0,0.15);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        input[type="range"]::-webkit-slider-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 3px 10px rgba(0,0,0,0.25);
        }

        input[type="range"]::-webkit-slider-thumb:active {
          transform: scale(1.1);
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.5);
        }

        input[type="range"]::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: white;
          cursor: pointer;
          border: 2px solid #3b82f6;
          box-shadow: 0 2px 6px rgba(0,0,0,0.15);
          transition: transform 0.2s ease, box-shadow 0.2s ease;
        }

        input[type="range"]::-moz-range-thumb:hover {
          transform: scale(1.2);
          box-shadow: 0 3px 10px rgba(0,0,0,0.25);
        }

        .dark input[type="range"]::-webkit-slider-thumb {
          background: #cdd6f4;
          border-color: #89b4fa;
        }

        .dark input[type="range"]::-moz-range-thumb {
          background: #cdd6f4;
          border-color: #89b4fa;
        }

        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes slide-in-from-top-2 {
          from { transform: translateY(-8px); }
          to { transform: translateY(0); }
        }

        @keyframes slide-in-from-top-1 {
          from { transform: translateY(-4px); }
          to { transform: translateY(0); }
        }

        .animate-in {
          animation: fade-in 0.3s ease-out, slide-in-from-top-2 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default DailyView;
